import {useMemo} from 'react';

import {type PanelBankFlowSectionConfig} from '../components/PanelBank/types';
import {
  isEvaluationSectionName,
  isEvaluationTablesSectionName,
  isMediaSectionName,
  isTableSectionName,
} from './panelbankConfigs';
import {TABLE_SECTION_HEIGHT} from './panelbankTypes';

const DEFAULT_HEIGHT = 300;

const MEDIA_SECTION_HEIGHT = 375;

// Fits a 10-row evaluation table without causing scroll-in-scroll
const EVALUATION_SECTION_HEIGHT = 558;

// Fits 4 rows of eval results when comparing two evals with medium row height
const EVALUATION_TABLES_SECTION_HEIGHT = 800;

export const DEFAULT_FLOW_CONFIG_BOX_WIDTH = 460;

export const DEFAULT_MOBILE_COLUMNS_PER_PAGE = 1;
export const DEFAULT_MOBILE_PANELS_PER_PAGE = 1;

const getBoxHeight = (sectionName?: string) => {
  if (isTableSectionName(sectionName)) {
    return TABLE_SECTION_HEIGHT;
  }
  if (isMediaSectionName(sectionName)) {
    return MEDIA_SECTION_HEIGHT;
  }
  if (isEvaluationSectionName(sectionName)) {
    return EVALUATION_SECTION_HEIGHT;
  }
  if (isEvaluationTablesSectionName(sectionName)) {
    return EVALUATION_TABLES_SECTION_HEIGHT;
  }
  return DEFAULT_HEIGHT;
};

export const getDefaultFlowConfig = (
  sectionName?: string
): PanelBankFlowSectionConfig => {
  return {
    snapToColumns: true,
    columnsPerPage:
      isMediaSectionName(sectionName) ||
      isEvaluationTablesSectionName(sectionName) ||
      isEvaluationSectionName(sectionName)
        ? 1
        : 3,
    rowsPerPage: 2,
    mobileColumnsPerPage: DEFAULT_MOBILE_COLUMNS_PER_PAGE,
    mobileRowsPerPage: DEFAULT_MOBILE_PANELS_PER_PAGE,
    /** @deprecated - there's no longer a need to use this. we rely on grid css to handle spacing now */
    gutterWidth: 16,
    boxWidth: DEFAULT_FLOW_CONFIG_BOX_WIDTH,
    boxHeight: getBoxHeight(sectionName),
  };
};

export const getFullFlowConfig = (
  sectionName: string,
  flowConfig?: Partial<PanelBankFlowSectionConfig>
): PanelBankFlowSectionConfig => {
  return {
    ...getDefaultFlowConfig(sectionName),
    ...flowConfig,
  };
};

export const useFullFlowConfig = (
  sectionName: string,
  flowConfig?: Partial<PanelBankFlowSectionConfig>
): PanelBankFlowSectionConfig => {
  return useMemo(
    () => getFullFlowConfig(sectionName, flowConfig),
    [sectionName, flowConfig]
  );
};

export const compressFlowConfig = (
  sectionName: string,
  flowConfig?: Partial<PanelBankFlowSectionConfig>
): Partial<PanelBankFlowSectionConfig> | undefined => {
  if (!flowConfig) {
    return flowConfig;
  }
  const defaultConfig = getDefaultFlowConfig(sectionName);
  return Object.keys(flowConfig).reduce(
    (acc, cur) => {
      const key = cur as keyof typeof flowConfig;
      if (flowConfig[key] === defaultConfig[key]) {
        return acc;
      }
      return {
        ...acc,
        [key]: flowConfig[key],
      };
    },
    undefined as Partial<PanelBankFlowSectionConfig> | undefined
  );
};
